import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Sparkles } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useTheme } from "@mui/material";
import {
  INPUT_COLOR_BORDER,
  BOX_SHADOW_THEME,
  GRADIENT_BLACK_COLOR,
} from "../../utils/colors";
import {
  CHAT_BOT_WELCOME_DESCRIPTION,
  CHAT_BOT_WELCOME_MESSAGE,
  CHAT_BOT_WELCOME_MESSAGE_STATISTICS,
  CHAT_BOT_WELCOME_DESCRIPTION_STATISTICS,
  CHAT_BOT_WELCOME_MESSAGE_OWNER,
  CHAT_BOT_WELCOME_DESCRIPTION_OWNER,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import {
  BORDER_RADIUS,
  getFilteredCardsChatBot,
  OWNER_ROLE,
} from "../../utils/constants";

const ChatWelcomeDescription = ({
  campaign,
  onClickCard,
  singleColumn = false,
  agentContext,
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const location = useLocation();
  const isInStatisticsPage = location.pathname === "/ads-campaign-statistics";
  const isOwnerDashboard = agentContext === OWNER_ROLE;

  const filteredCards = getFilteredCardsChatBot({
    campaign,
    agentContext,
  });

  const showQuickQuestionCards =
    !isInStatisticsPage && filteredCards.length > 0;

  const titleBlockMarginBottom = showQuickQuestionCards ? 2 : 0;
  const descriptionMarginTop = showQuickQuestionCards ? 2 : 0;

  return (
    <Box
      sx={{
        py: 2,
        px: 1,
        width: { xs: "100%", lg: "90%" },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          mt: 0,
          mb: titleBlockMarginBottom,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
          <Box
            sx={{
              width: 55,
              height: 55,
              backgroundImage: GRADIENT_BLACK_COLOR,
              borderRadius: BORDER_RADIUS,
              display: "grid",
              placeItems: "center",
              overflow: "hidden",
            }}
          >
            <Sparkles size={26} color="#fff" />
          </Box>
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            mb: 0.5,
            textAlign: "center",
          }}
        >
          {t(
            isOwnerDashboard
              ? CHAT_BOT_WELCOME_MESSAGE_OWNER
              : isInStatisticsPage
                ? CHAT_BOT_WELCOME_MESSAGE_STATISTICS
                : CHAT_BOT_WELCOME_MESSAGE
          )}
        </Typography>
      </Box>

      {showQuickQuestionCards && (
        <Grid container spacing={1.5} alignItems="stretch" sx={{ pb: 2 }}>
          {filteredCards.map((capability, index) => {
            const Icon = capability.icon;
            return (
              <Grid
                key={index}
                item
                xs={12}
                sm={singleColumn ? 12 : 6}
                lg={singleColumn ? 12 : 4}
                sx={{ display: "flex" }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    backgroundColor: "fields.background",
                    border: `1px solid ${INPUT_COLOR_BORDER}`,
                    boxShadow: BOX_SHADOW_THEME,
                    borderRadius: `${BORDER_RADIUS * 4}px !important`,
                    cursor: "pointer",
                    transition: "transform 0.2s ease",
                    height: "100%",
                    width: "100%",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 80,
                    "&:hover": {
                      transform: "translateY(-1px)",
                    },
                  }}
                  onClick={() => onClickCard(capability.question)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Box>
                      <Icon size={16} color={theme.palette.text.primary} />
                    </Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        textAlign: "left",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t(capability.title)}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6, textAlign: "left" }}
                  >
                    {t(capability.description)}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: "center", mt: descriptionMarginTop }}
      >
        {t(
          isOwnerDashboard
            ? CHAT_BOT_WELCOME_DESCRIPTION_OWNER
            : isInStatisticsPage
              ? CHAT_BOT_WELCOME_DESCRIPTION_STATISTICS
              : CHAT_BOT_WELCOME_DESCRIPTION
        )}
      </Typography>
    </Box>
  );
};

export default ChatWelcomeDescription;
